import React, { useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { meetingHasStarted, postAttendanceUpdate } from "./AttendanceFlags";

const ACCEPT = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((part) => part[0].toUpperCase()).join("");
}

function fileKind(fileName) {
  const ext = String(fileName || "").split(".").pop().toLowerCase();
  if (ext === "pdf") return "PDF";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "Image";
  return "File";
}

function selectionOf(row) {
  if (Number(row.is_medical_leave) === 1) return "medical";
  if (Number(row.is_substitute) === 1) return "substitute";
  if (Number(row.meeting_status) === 1) return "present";
  if (Number(row.meeting_status) === 2) return "absent";
  return "";
}

function attachmentOf(row) {
  if (!row.attachment || typeof row.attachment !== "object" || !row.attachment.url) return null;
  return row.attachment;
}

function MedicalProof({ row, onUploaded, onRemoved }) {
  const attachment = attachmentOf(row);
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: false,
    maxFiles: 1,
    noClick: Boolean(attachment),
    noKeyboard: Boolean(attachment),
    accept: ACCEPT,
    onDrop: (files) => {
      if (files[0]) onUploaded(files[0]);
    },
    onDropRejected: () => toast.error("Only PDF and image files are allowed"),
  });

  const quietButton = {
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#374151",
    borderRadius: 8,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
  };

  return (
    <div
      {...getRootProps()}
      style={{
        marginTop: 6,
        marginLeft: 42,
        background: "#fff8ee",
        border: "1px solid #f3e4c4",
        borderRadius: 10,
        padding: "8px 10px",
      }}
    >
      <input {...getInputProps()} />
      <div style={{ fontSize: 12, fontWeight: 700, color: "#a16207", marginBottom: 6 }}>Medical proof</div>
      {attachment ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #f3e6cc",
            borderRadius: 8,
            padding: "8px 10px",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f8efdc", display: "flex", alignItems: "center", justifyContent: "center", color: "#a16207", flexShrink: 0 }}>
            <i className="bx bx-file"></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 650, fontSize: 14, color: "#1f2937", wordBreak: "break-all" }}>{attachment.file_name}</div>
            <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 2 }}>Uploaded · {fileKind(attachment.file_name)}</div>
          </div>
          <a href={attachment.url} target="_blank" rel="noreferrer" style={{ ...quietButton, textDecoration: "none" }}>View</a>
          <button type="button" style={quietButton} onClick={(event) => { event.stopPropagation(); open(); }}>Replace</button>
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); onRemoved(); }}
            style={{ ...quietButton, color: "#dc2626", width: 32, padding: "5px 0" }}
          >
            <i className="bx bx-trash"></i>
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#a16207", cursor: "pointer", padding: "2px 0" }}>
          {isDragActive ? "Drop the file here" : "Upload a PDF or image. This is optional."}
        </div>
      )}
    </div>
  );
}

const MeetingAttendancePanel = ({ loading, meetingDate, meetingTime, registrys, onClose, onRefresh }) => {
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [substituteForm, setSubstituteForm] = useState(null);
  const rows = useMemo(() => {
    const list = Array.isArray(registrys) ? [...registrys] : [];
    return list.sort((a, b) =>
      String(a.user_name || "").localeCompare(String(b.user_name || ""), undefined, { sensitivity: "base" })
    );
  }, [registrys]);

  const counts = useMemo(() => {
    return rows.reduce((total, row) => {
      const selected = selectionOf(row);
      if (selected === "present") total.present += 1;
      else if (selected === "substitute") total.substitute += 1;
      else if (selected === "medical") total.medical += 1;
      else if (selected === "absent") total.absent += 1;
      else total.unmarked += 1;
      return total;
    }, { present: 0, substitute: 0, medical: 0, absent: 0, unmarked: 0 });
  }, [rows]);

  const visibleRows = rows.filter((row) =>
    String(row.user_name || "").toLowerCase().includes(search.trim().toLowerCase())
  );
  const marked = rows.length - counts.unmarked;
  const started = meetingHasStarted(meetingDate, meetingTime);

  const run = (scheduleId, fields, file) => {
    setBusy(true);
    return postAttendanceUpdate(scheduleId, fields, file)
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          toast.success(response.data.msg);
          if (onRefresh) onRefresh();
        } else {
          toast.error(response.data.msg || "Failed to update attendance");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.msg || "Something went wrong");
      })
      .finally(() => setBusy(false));
  };

  const markEveryonePresent = () => {
    const pending = rows.filter((row) => selectionOf(row) !== "present");
    if (!pending.length) return;
    setBusy(true);
    Promise.all(pending.map((row) => postAttendanceUpdate(row.id, { status: 1 })))
      .then(() => {
        toast.success("Attendance updated successfully");
        if (onRefresh) onRefresh();
      })
      .catch((err) => {
        toast.error(err.response?.data?.msg || "Something went wrong");
      })
      .finally(() => setBusy(false));
  };

  const choose = (row, next) => {
    if (!started || busy) return;
    if (next === "substitute") {
      setSubstituteForm({
        id: row.id,
        name: row.substitute_name || "",
        mobile: row.substitute_mobile || "",
      });
      return;
    }
    if (selectionOf(row) === next) return;
    setSubstituteForm(null);
    if (next === "present") run(row.id, { status: 1 });
    if (next === "medical") run(row.id, { is_medical_leave: 1 });
    if (next === "absent") run(row.id, { status: 2, is_substitute: 0, is_medical_leave: 0 });
  };

  const saveSubstitute = (row) => {
    const name = String(substituteForm?.name || "").trim();
    const mobile = String(substituteForm?.mobile || "").trim();
    if (!name) {
      toast.error("Substitute name is required");
      return;
    }
    if (!/^[0-9]{10}$/.test(mobile)) {
      toast.error("Substitute mobile number must be 10 digits");
      return;
    }
    run(row.id, { is_substitute: 1, substitute_name: name, substitute_mobile: mobile })
      .then(() => setSubstituteForm(null));
  };

  const cards = [
    { label: "Present", value: counts.present, background: "#e7f6ee", color: "#1f7a45" },
    { label: "Substitute", value: counts.substitute, background: "#eef0ff", color: "#3d4db8" },
    { label: "Medical leave", value: counts.medical, background: "#f8efdc", color: "#9a6b12" },
    { label: "Absent", value: counts.absent, background: "#fee2e2", color: "#b91c1c" },
    { label: "Not marked yet", value: counts.unmarked, background: "#f3f4f6", color: "#4b5563" },
  ];

  return (
    <>
      <Modal.Header closeButton style={{ borderBottom: "none", alignItems: "flex-start", padding: "22px 24px 8px" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 750, color: "#111827", letterSpacing: "-0.02em" }}>Meeting Attendance</div>
            <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
            {meetingDate || "-"} · {meetingTime || "-"}
          </div>
          {!started && (
            <div style={{ color: "#b45309", fontSize: 13, marginTop: 8 }}>
              Attendance can be marked after the meeting starts.
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: "8px 24px 12px" }}>
        {loading ? (
          <div className="text-center text-muted py-4">Loading attendance...</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 18 }}>
              {cards.map((card) => (
                <div key={card.label} style={{ background: card.background, borderRadius: 14, padding: "14px 16px" }}>
                  <div style={{ fontSize: 26, fontWeight: 750, color: card.color, lineHeight: 1.1 }}>{card.value}</div>
                  <div style={{ fontSize: 13, color: card.color, marginTop: 4 }}>{card.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ position: "relative", width: 280, maxWidth: "100%", flex: "0 0 auto" }}>
                <i className="bx bx-search" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: 18 }}></i>
                <input
                  placeholder="Search member"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  style={{
                    width: "100%",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: "11px 14px 11px 40px",
                    fontSize: 14,
                    outline: "none",
                    background: "#fff",
                  }}
                />
              </div>
              <button
                type="button"
                disabled={busy || !started}
                onClick={markEveryonePresent}
                style={{
                  border: "1px solid #86efac",
                  background: "#fff",
                  color: "#15803d",
                  borderRadius: 12,
                  padding: "11px 16px",
                  fontSize: 14,
                  fontWeight: 650,
                  whiteSpace: "nowrap",
                }}
              >
                ✓ Mark everyone present
              </button>
            </div>
            <div style={{ maxHeight: 480, overflowY: "auto", marginTop: 4 }}>
            {visibleRows.length === 0 ? (
              <div className="text-center text-muted py-4">No members found</div>
            ) : visibleRows.map((row) => {
              const selected = selectionOf(row);
              const statusColor = selected === "present" ? "#15803d" : selected === "substitute" ? "#3730a3" : selected === "medical" ? "#a16207" : selected === "absent" ? "#b91c1c" : "#9ca3af";
              return (
                <div key={row.id} style={{ borderTop: "1px solid #f1f3f5", padding: "7px 2px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#f8d7df", color: "#9f1239", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>
                      {initials(row.user_name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: "#1f2937", fontSize: 13, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.user_name}</div>
                      <div style={{ fontSize: 11, marginTop: 1, color: statusColor, fontWeight: selected ? 650 : 500 }}>
                        {selected === "present" ? "Present" : selected === "substitute" ? "Substitute" : selected === "medical" ? "Medical leave" : selected === "absent" ? "Absent" : "Not marked"}
                      </div>
                    </div>
                    <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 9, padding: 2, gap: 2, flexShrink: 0 }}>
                      {[
                        ["present", "Present", "#dcfce7", "#15803d", "#86efac"],
                        ["substitute", "Substitute", "#e0e7ff", "#3730a3", "#a5b4fc"],
                        ["medical", "Medical leave", "#f8e7c3", "#a16207", "#e8c36a"],
                        ["absent", "Absent", "#fee2e2", "#b91c1c", "#fca5a5"],
                      ].map(([key, label, background, color, border]) => {
                        const active = selected === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            disabled={busy || !started}
                            onClick={() => choose(row, key)}
                            style={{
                              border: active ? `1px solid ${border}` : "1px solid transparent",
                              background: active ? background : "transparent",
                              color: active ? color : "#6b7280",
                              borderRadius: 7,
                              padding: "4px 9px",
                              fontSize: 12,
                              fontWeight: active ? 700 : 600,
                              lineHeight: 1.2,
                            }}
                          >
                            {active ? "✓ " : ""}{label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {(substituteForm?.id === row.id || (selected === "substitute" && Boolean(String(row.substitute_name || "").trim() || String(row.substitute_mobile || "").trim()))) && (
                    <div style={{ marginTop: 6, marginLeft: 42, background: "#f5f7ff", border: "1px solid #e0e7ff", borderRadius: 10, padding: "8px 10px" }}>
                      {substituteForm?.id === row.id ? (
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 160px auto auto", gap: 8, alignItems: "center" }}>
                          <input
                            value={substituteForm.name}
                            placeholder="Name"
                            onChange={(event) => setSubstituteForm({ ...substituteForm, name: event.target.value })}
                            style={{ border: "1px solid #dbe1f1", background: "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 13, color: "#111827", minWidth: 0 }}
                          />
                          <input
                            value={substituteForm.mobile}
                            placeholder="Mobile"
                            inputMode="numeric"
                            maxLength={10}
                            onChange={(event) => setSubstituteForm({ ...substituteForm, mobile: event.target.value.replace(/\D/g, "").slice(0, 10) })}
                            style={{ border: "1px solid #dbe1f1", background: "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 13, color: "#111827" }}
                          />
                          <button type="button" disabled={busy} onClick={() => saveSubstitute(row)} style={{ border: "none", background: "#3730a3", color: "#fff", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 700 }}>Save</button>
                          <button type="button" onClick={() => setSubstituteForm(null)} style={{ border: "1px solid #e5e7eb", background: "#fff", color: "#4b5563", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 650 }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ background: "#fff", border: "1px solid #e6e9f5", borderRadius: 8, padding: "8px 10px", display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#eef2ff", color: "#3730a3", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <i className="bx bx-user"></i>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 650, fontSize: 13, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.substitute_name || "-"}</div>
                            <div style={{ color: "#6b7280", fontSize: 12, marginTop: 1 }}>{row.substitute_mobile || "-"}</div>
                          </div>
                          <button
                            type="button"
                            title="Edit"
                            onClick={() => setSubstituteForm({ id: row.id, name: row.substitute_name || "", mobile: row.substitute_mobile || "" })}
                            style={{ border: "1px solid #e5e7eb", background: "#fff", color: "#3730a3", borderRadius: 8, width: 32, height: 32, padding: 0 }}
                          >
                            <i className="bx bx-edit-alt"></i>
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            disabled={busy}
                            onClick={() => run(row.id, { is_substitute: 0, status: 1 })}
                            style={{ border: "1px solid #e5e7eb", background: "#fff", color: "#dc2626", borderRadius: 8, width: 32, height: 32, padding: 0 }}
                          >
                            <i className="bx bx-trash"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {selected === "medical" && (
                    <MedicalProof
                      row={row}
                      onUploaded={(file) => run(row.id, {}, file)}
                      onRemoved={() => run(row.id, { remove_attachment: 1 })}
                    />
                  )}
                </div>
              );
            })}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between", borderTop: "1px solid #f1f3f5", padding: "14px 24px" }}>
        <span style={{ color: "#6b7280", fontSize: 13 }}>{marked} of {rows.length} members marked</span>
        <button type="button" onClick={onClose} style={{ border: "none", background: "#e11d2e", color: "#fff", borderRadius: 12, padding: "10px 18px", fontWeight: 650 }}>Close</button>
      </Modal.Footer>
    </>
  );
};

export default MeetingAttendancePanel;
